import { useUser } from "@/auth/provider/UserContext";
import { ProgramResponse, useViaductApi } from "@/viaduct-api";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface CurrentProgramContextValue {
  /** All programs in the current org (loaded once on mount + on refresh()). */
  programs: ProgramResponse[];
  /** The currently-selected program, if any. Null when no programs exist or
   *  the user explicitly cleared the selection. */
  currentProgram: ProgramResponse | null;
  /** Programmatically select a program by id. Persists to localStorage. */
  setCurrentProgramId: (programId: string | null) => void;
  /** Re-fetch the program list (e.g. after creating a new program). */
  refresh: () => Promise<void>;
  loading: boolean;
}

const noopCtx: CurrentProgramContextValue = {
  programs: [],
  currentProgram: null,
  setCurrentProgramId: () => {},
  refresh: async () => {},
  loading: false,
};

const CurrentProgramContext =
  createContext<CurrentProgramContextValue>(noopCtx);

const storageKey = (orgId: string, userId: string) =>
  `viaduct.currentProgramId.${orgId}.${userId}`;

/** Read the persisted program id for this (org, user) from localStorage. */
const readStoredId = (orgId: string, userId: string): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(storageKey(orgId, userId));
  } catch {
    return null;
  }
};

const writeStoredId = (
  orgId: string,
  userId: string,
  id: string | null,
): void => {
  if (typeof window === "undefined") return;
  try {
    if (id) {
      window.localStorage.setItem(storageKey(orgId, userId), id);
    } else {
      window.localStorage.removeItem(storageKey(orgId, userId));
    }
  } catch {
    // ignore quota / privacy mode errors
  }
};

const newestProgram = (
  programs: ProgramResponse[],
): ProgramResponse | null => {
  if (programs.length === 0) return null;
  // ProgramResponse has created_at as an ISO string.
  const sorted = [...programs].sort((a, b) =>
    (b.created_at ?? "").localeCompare(a.created_at ?? ""),
  );
  return sorted[0];
};

export const CurrentProgramProvider = ({ children }: PropsWithChildren) => {
  const { orgUser } = useUser();
  const { client } = useViaductApi();
  const [programs, setPrograms] = useState<ProgramResponse[]>([]);
  const [currentProgramId, setCurrentProgramIdState] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const orgId = orgUser?.org?.id ?? "";
  const userId = orgUser?.user?.id ?? "";

  const fetchPrograms = useCallback(async (): Promise<ProgramResponse[]> => {
    if (!orgId) return [];
    setLoading(true);
    try {
      const result = await client.orgProgramListAllPrograms({
        path: { org_id: orgId },
      });
      return result.data ?? [];
    } finally {
      setLoading(false);
    }
  }, [client, orgId]);

  const refresh = useCallback(async () => {
    const next = await fetchPrograms();
    setPrograms(next);
    // If the current selection is no longer present, fall back to newest.
    if (currentProgramId && !next.some((p) => p.id === currentProgramId)) {
      const fallback = newestProgram(next);
      setCurrentProgramIdState(fallback?.id ?? null);
      writeStoredId(orgId, userId, fallback?.id ?? null);
    }
  }, [fetchPrograms, currentProgramId, orgId, userId]);

  // Initial load: hydrate from localStorage, fetch programs, validate.
  useEffect(() => {
    if (!orgId || !userId) return;
    let cancelled = false;
    void (async () => {
      const stored = readStoredId(orgId, userId);
      const fetched = await fetchPrograms();
      if (cancelled) return;
      setPrograms(fetched);
      const exists = stored && fetched.some((p) => p.id === stored);
      const initial = exists ? stored : (newestProgram(fetched)?.id ?? null);
      setCurrentProgramIdState(initial);
      if (initial !== stored) {
        writeStoredId(orgId, userId, initial);
      }
      setHydrated(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, userId, fetchPrograms]);

  const setCurrentProgramId = useCallback(
    (programId: string | null) => {
      setCurrentProgramIdState(programId);
      writeStoredId(orgId, userId, programId);
    },
    [orgId, userId],
  );

  const currentProgram = useMemo(
    () => programs.find((p) => p.id === currentProgramId) ?? null,
    [programs, currentProgramId],
  );

  const value = useMemo<CurrentProgramContextValue>(
    () => ({
      programs,
      currentProgram,
      setCurrentProgramId,
      refresh,
      loading: loading || !hydrated,
    }),
    [programs, currentProgram, setCurrentProgramId, refresh, loading, hydrated],
  );

  return (
    <CurrentProgramContext.Provider value={value}>
      {children}
    </CurrentProgramContext.Provider>
  );
};

/** Hook to read & set the current program. Safe to call outside the
 *  provider; returns a no-op context. */
export const useCurrentProgram = () => useContext(CurrentProgramContext);
